import styled from "styled-components";

import { media } from "../../../../../../styles/media";

export const TimelineBodyStyles = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.semantic.container.white};

  /* On mobile let the name column and the timeline scroll horizontally as one
     unit (instead of a fixed name column + a separately-scrolling hour grid),
     so the whole schedule can be swiped to reveal every hour. */
  ${media.maxTablet`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    .timeline {
      flex: none;
      width: max-content;
    }
  `}

  /* ----- Left name column ----- */
  .name-col {
    width: 200px;
    flex-shrink: 0;
    border-right: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.semantic.container.white};
  }

  .name-head {
    height: 40px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    align-items: center;
    padding: 0 14px;
    font-size: 10px;
    color: ${({ theme }) => theme.colors.primary[400]};
    text-transform: uppercase;
    letter-spacing: 1.2px;
    justify-content: space-between;
  }

  .name-list {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  /* Unassigned section header (left side) */
  .unassigned-head-name {
    height: 28px;
    background: ${({ theme }) => theme.colors.accent[50]};
    border-bottom: 1px solid ${({ theme }) => theme.colors.accent[150]};
    display: flex;
    align-items: center;
    padding: 0 14px;
    font-size: 10.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.accent[600]};
    letter-spacing: 1px;
    text-transform: uppercase;
    justify-content: space-between;

    .hint {
      color: ${({ theme }) => theme.colors.accent[500]};
      font-size: 10px;
      text-transform: none;
      letter-spacing: 0;
      font-weight: 500;
    }

    &.drop-active {
      background: ${({ theme }) => theme.colors.accent[100]};
    }
  }

  .unassigned-row-name {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 14px;
    background: ${({ theme }) => theme.colors.primary[50]};

    &.drop-active {
      background: ${({ theme }) => theme.colors.accent[50]};
      outline: 1.5px dashed ${({ theme }) => theme.colors.accent[500]};
      outline-offset: -2px;
    }
  }

  .priority-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    display: inline-block;
    flex-shrink: 0;

    &.high { background: ${({ theme }) => theme.colors.error[500]}; }
    &.normal { background: ${({ theme }) => theme.colors.accent[500]}; }
    &.low { background: ${({ theme }) => theme.colors.primary[400]}; }
  }

  .un-meta {
    min-width: 0;
    flex: 1;

    .type {
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    .id-truck {
      font-size: 10px;
      color: ${({ theme }) => theme.colors.primary[500]};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .section-spacer {
    height: 8px;
    background: ${({ theme }) => theme.colors.semantic.background.white};
    border-bottom: 3px solid ${({ theme }) => theme.colors.primary[100]};
  }

  .worker-row-name {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px 0 8px;
    cursor: pointer;
    background: ${({ theme }) => theme.colors.semantic.container.white};

    .chev {
      color: ${({ theme }) => theme.colors.primary[400]};
      width: 14px;
      display: grid;
      place-items: center;
    }

    .meta {
      min-width: 0;
      flex: 1;

      .name {
        font-size: 12.5px;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${({ theme }) => theme.colors.semantic.text.strong};
      }

      .sub {
        font-size: 10.5px;
        color: ${({ theme }) => theme.colors.primary[400]};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .count {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.primary[600]};
      font-variant-numeric: tabular-nums;
      font-weight: 500;
    }
  }

  /* ----- Right scrolling timeline ----- */
  .timeline {
    flex: 1;
    overflow: auto;
    position: relative;
  }

  .timeline-inner {
    position: relative;
  }

  .hour-head {
    height: 40px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.semantic.container.white};
    z-index: 5;
  }

  .hour-cell {
    border-right: 1px solid ${({ theme }) => theme.colors.primary[100]};
    display: flex;
    align-items: center;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.primary[500]};
    flex-shrink: 0;
  }

  .row-grid {
    position: absolute;
    top: 0;
    bottom: 0;
    border-right: 1px solid ${({ theme }) => theme.colors.primary[100]};

    .half {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 1px;
      background: repeating-linear-gradient(
        to bottom,
        #f4f4f4 0 3px,
        transparent 3px 6px
      );
    }
  }

  .lunch {
    position: absolute;
    top: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      135deg,
      #fafafa 0 6px,
      transparent 6px 12px
    );
    pointer-events: none;
  }

  .now-line {
    position: absolute;
    top: 40px;
    bottom: 0;
    width: 2px;
    background: ${({ theme }) => theme.colors.accent[500]};
    z-index: 3;
    pointer-events: none;

    .now-dot {
      position: absolute;
      top: 0;
      left: -4px;
      width: 10px;
      height: 10px;
      border-radius: 999px;
      background: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  .unassigned-head {
    height: 28px;
    background: ${({ theme }) => theme.colors.accent[50]};
    border-bottom: 1px solid ${({ theme }) => theme.colors.accent[150]};
  }

  .unassigned-row {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    position: relative;
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  .un-chip {
    position: absolute;
    background: ${({ theme }) => theme.colors.semantic.background.white};
    border: 1.5px dashed ${({ theme }) => theme.colors.accent[500]};
    border-radius: 6px;
    padding: 0 9px;
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    &:active {
      cursor: grabbing;
    }

    .un-chip-meta {
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 8px;

      .type {
        font-size: 11.5px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.accent[600]};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .truck {
        font-size: 10px;
        color: ${({ theme }) => theme.colors.primary[600]};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .stack {
        min-width: 0;
      }
    }

    .un-chip-time {
      font-size: 10.5px;
      color: ${({ theme }) => theme.colors.accent[600]};
      font-variant-numeric: tabular-nums;
      font-weight: 500;
      flex-shrink: 0;
    }
  }

  .worker-row {
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary[100]};
    position: relative;
    background: ${({ theme }) => theme.colors.semantic.container.white};
  }

  .drop-target {
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.colors.accent[500] + "0c"};
    border: 1.5px dashed ${({ theme }) => theme.colors.accent[500]};
    border-radius: 6px;
    pointer-events: none;
  }

  .compact-bar {
    position: absolute;
    top: 10px;
    height: 16px;
    background: var(--bar-color);
    opacity: 0.75;
    border-radius: 3px;
  }

  .job {
    position: absolute;
    height: 44px;
    background: var(--job-bg);
    border-left: 3px solid var(--job-accent);
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .row1 {
      display: flex;
      justify-content: space-between;
      gap: 6px;

      .type {
        font-weight: 600;
        color: var(--job-fg);
        font-size: 11.5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .warn {
        color: ${({ theme }) => theme.colors.error[500]};
        display: flex;
        align-items: center;
      }
    }

    .row2 {
      display: flex;
      justify-content: space-between;
      color: var(--job-fg);
      opacity: 0.85;
      font-size: 10.5px;

      .truck {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .start {
        font-weight: 500;
        font-variant-numeric: tabular-nums;
      }
    }

    &.conflict {
      border-left-color: ${({ theme }) => theme.colors.error[500]};
    }

    &[draggable="true"] {
      cursor: grab;
    }

    &.dragging {
      opacity: 0.4;
      cursor: grabbing;
    }
  }

  .placeholder {
    height: 200px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.primary[400]};
  }
`;
